<template>
  <div
    class="bv2"
    :class="{
      'is-busy': busy,
      'has-sheet': !!(sheet || ask),
      'is-ready': ready,
      'has-dock': showDock
    }"
  >
    <div v-if="busy" class="bv2-progress" aria-hidden="true"><i /></div>

    <header class="bv2-top bv2-top-desktop">
      <div class="bv2-bar">
        <button class="bv2-chev" type="button" aria-label="Previous day" @click="shiftDay(-1)">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div class="bv2-bar-copy">
          <div class="bv2-kicker">
            <span>{{ shopName }}</span>
            <i>Beta</i>
          </div>
          <h1 class="bv2-title">{{ dayTitle }}</h1>
          <p class="bv2-sub">{{ dayInsightText }}</p>
        </div>
        <button class="bv2-chev" type="button" aria-label="Next day" @click="shiftDay(1)">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9.5 5.5L16 12l-6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div class="bv2-bar-actions">
          <button
            v-if="selectedDate !== today"
            class="bv2-chip-btn"
            type="button"
            @click="goToday"
          >
            Today
          </button>
          <button
            v-if="selectedDate === today"
            class="bv2-chip-btn"
            type="button"
            :class="{ 'is-on': followNow }"
            @click="jumpNow"
          >
            Now
          </button>
          <button class="bv2-chip-btn is-strong" type="button" :disabled="busy || !staff.length" @click="openBook()">
            Book
          </button>
          <button class="bv2-chip-btn" type="button" :disabled="busy" @click="openFind">
            Find
          </button>
          <router-link class="bv2-classic" to="/bookings">Classic calendar</router-link>
        </div>
      </div>
    </header>

    <header class="bv2-phone">
      <div class="bv2-phone-week">
        <div class="bv2-phone-week-copy">
          <span>{{ shopName }}</span>
          <transition :name="dayMotion" mode="out-in">
            <strong :key="selectedDate">{{ dayTitle }}</strong>
          </transition>
        </div>
        <div v-click-outside="closeMore" class="bv2-more">
          <button
            class="bv2-more-btn"
            type="button"
            aria-label="More actions"
            :aria-expanded="moreOpen ? 'true' : 'false'"
            @click="moreOpen = !moreOpen"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="6" cy="12" r="1.7" fill="currentColor" />
              <circle cx="12" cy="12" r="1.7" fill="currentColor" />
              <circle cx="18" cy="12" r="1.7" fill="currentColor" />
            </svg>
          </button>
          <transition name="bv2-pop">
            <div v-if="moreOpen" class="bv2-more-menu" role="menu">
              <button v-if="selectedDate !== today" type="button" role="menuitem" @click="jumpToday">
                Jump to today
              </button>
              <button v-if="selectedDate === today" type="button" role="menuitem" @click="runNow">
                Jump to now
              </button>
              <button type="button" role="menuitem" :disabled="busy || !staff.length" @click="runBook">
                Book
              </button>
              <button type="button" role="menuitem" :disabled="busy" @click="runFind">
                Find
              </button>
              <router-link to="/bookings" role="menuitem" @click.native="closeMore">
                Classic calendar
              </router-link>
            </div>
          </transition>
        </div>
      </div>
      <p class="bv2-phone-status">{{ dayInsightText }}</p>
    </header>

    <nav class="bv2-strip" aria-label="Week">
      <button
        v-for="date in week"
        :key="'d-' + date"
        type="button"
        class="bv2-strip-day"
        :class="stripClass(date)"
        @click="selectDate(date)"
      >
        <span>{{ shortDay(date) }}</span>
        <b>{{ monthDay(date) }}</b>
        <em v-if="dayCounts[date] && dayCounts[date].total">{{ dayCounts[date].total }}</em>
        <em v-else-if="!isOpen(date)" class="is-shut">Shut</em>
      </button>
    </nav>

    <div v-if="loading" class="bv2-loading">Loading the book…</div>

    <div v-else-if="!staff.length" class="bv2-empty">
      Add staff in Employees first, then come back to fill the book.
    </div>

    <template v-else>
      <section
        v-if="mode === 'board'"
        ref="board"
        class="bv2-board"
        aria-label="Day book"
        @scroll.passive="onBoardScroll"
      >
        <div v-if="!dayStaff.length" class="bv2-board-empty">
          <strong>{{ isOpen(selectedDate) ? "Nobody on the book" : "Shop is closed" }}</strong>
          <span>People only show here when they have a booking.</span>
        </div>
        <div v-else class="bv2-grid" :style="gridStyle">
          <div class="bv2-corner">
            <span>{{ window.open ? window.start + "–" + window.end : "Closed" }}</span>
          </div>
          <div
            v-for="person in dayStaff"
            :key="'h-' + person._id"
            class="bv2-colhead"
            :style="laneStyle(person._id)"
          >
            <div class="bv2-face" :style="{ background: tone(person._id) }">
              <img
                v-if="person.image && !brokenImages[person._id]"
                :src="person.image"
                :alt="person.name"
                @error="markBroken(person._id)"
              />
              <span v-else>{{ staffInitials(person.name) }}</span>
            </div>
            <div>
              <strong>{{ person.name }}</strong>
              <span>{{ columnCount(person._id) }}</span>
            </div>
          </div>

          <div class="bv2-hours" aria-hidden="true">
            <i
              v-for="mark in marks"
              :key="mark.time"
              class="bv2-hour"
              :class="{ 'is-hour': mark.hour, 'is-half': !mark.hour }"
              :style="{ top: mark.pct + '%' }"
            >{{ mark.hour ? mark.time : "" }}</i>
            <b
              v-if="selectedDate === today && nowPct != null"
              class="bv2-now-label"
              :style="{ top: nowPct + '%' }"
            >{{ nowTick }}</b>
          </div>

          <div
            v-for="person in dayStaff"
            :key="'c-' + person._id"
            class="bv2-col"
            :style="laneStyle(person._id)"
            @click="onColumnClick($event, person)"
          >
            <div class="bv2-col-rail">
              <i
                v-for="mark in marks"
                :key="'l-' + person._id + mark.time"
                class="bv2-hline"
                :class="{ 'is-hour': mark.hour, 'is-half': !mark.hour }"
                :style="{ top: mark.pct + '%' }"
              />
              <i
                v-if="selectedDate === today && nowPct != null"
                class="bv2-now"
                :style="{ top: nowPct + '%' }"
              />
              <button
                v-for="item in columnBookings(person._id)"
                :key="item._id"
                type="button"
                class="bv2-appt"
                :class="apptClass(item)"
                :style="cardStyle(item)"
                :title="cardAria(item, person)"
                :aria-label="cardAria(item, person)"
                @click.stop="openBooking(item)"
              >
                <span class="bv2-appt-time">{{ timeRangeLabel(item) }}</span>
                <b class="bv2-appt-name">{{ customerName(item) }}</b>
                <span class="bv2-appt-work">{{ cardService(item) }}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section v-else class="bv2-dayboard" aria-label="Day book">
        <button
          v-if="!dayList.length"
          class="bv2-empty-card"
          type="button"
          :disabled="busy"
          @click="openBook()"
        >
          <strong>{{ isOpen(selectedDate) ? "Nothing booked" : "Shop is closed" }}</strong>
          <span>Tap to add a booking</span>
        </button>
        <button
          v-for="item in dayList"
          :key="'p-' + item._id"
          type="button"
          class="bv2-card"
          :class="'is-' + bookingKind(item)"
          :data-booking="item._id"
          :style="laneStyle(item.resourceId)"
          @click="openBooking(item)"
        >
          <div class="bv2-card-when">
            <b>{{ timeRangeLabel(item) }}</b>
            <span>{{ bookingKindLabel(bookingKind(item)) }}</span>
          </div>
          <div class="bv2-card-who">
            <strong>{{ customerName(item) }}</strong>
            <span>{{ cardService(item) }}</span>
          </div>
          <div
            class="bv2-face is-card"
            :style="{ background: tone(item.resourceId) }"
            :aria-label="staffName(item.resourceId)"
          >
            <img
              v-if="staffImage(item.resourceId)"
              :src="staffImage(item.resourceId)"
              :alt="staffName(item.resourceId)"
              @error="markBroken(item.resourceId)"
            />
            <span v-else>{{ staffInitials(staffName(item.resourceId)) }}</span>
          </div>
        </button>
      </section>
    </template>

    <section
      v-if="showDock"
      class="bv2-dock is-armed"
      aria-label="Bookings to confirm"
    >
      <div class="bv2-dock-bar">
        <p>
          <strong>{{ incoming.length }}</strong>
          {{ incoming.length === 1 ? "booking to confirm" : "bookings to confirm" }}
        </p>
      </div>
      <div class="bv2-dock-list">
        <button
          v-for="item in incoming"
          :key="'q-' + item._id"
          type="button"
          class="bv2-dock-item"
          @click="openBooking(item)"
        >
          <b>{{ customerName(item) }}</b>
          <span>{{ shortWhen(item) }} · {{ staffName(item.resourceId) }}</span>
        </button>
      </div>
    </section>

    <transition name="bv2-overlay">
      <div v-if="ask" class="bv2-scrim" @click.self="closeAsk">
        <div class="bv2-sheet bv2-ask" role="dialog" aria-modal="true" :aria-label="ask.title">
          <p class="bv2-sheet-kicker">{{ ask.kicker }}</p>
          <h2>{{ ask.title }}</h2>
          <p class="bv2-ask-body">{{ ask.body }}</p>
          <button
            type="button"
            class="bv2-primary"
            :class="{ 'is-danger': ask.action === 'cancel' }"
            :disabled="busy"
            @click="confirmAsk"
          >
            {{ ask.actionLabel }}
          </button>
          <button type="button" class="bv2-close" :disabled="busy" @click="closeAsk">
            Back
          </button>
        </div>
      </div>
    </transition>

    <transition name="bv2-overlay">
      <div v-if="sheet && sheet.type === 'booking'" class="bv2-scrim" @click.self="closeSheet">
        <div
          class="bv2-sheet"
          role="dialog"
          aria-modal="true"
          :aria-label="sheetCustomer"
        >
          <button type="button" class="bv2-sheet-handle" aria-label="Close" @click="closeSheet">
            <i />
          </button>
          <header class="bv2-sheet-head">
            <div class="bv2-face is-lg" :style="{ background: tone(sheetBooking.resourceId) }">
              <span>{{ staffInitials(sheetCustomer) }}</span>
            </div>
            <div>
              <p class="bv2-sheet-kicker">{{ sheetWhen }}</p>
              <h2>{{ sheetCustomer }}</h2>
              <p class="bv2-sheet-now">{{ bookingKindLabel(bookingKind(sheetBooking)) }}</p>
            </div>
          </header>

          <dl class="bv2-facts">
            <div v-if="cardPackage(sheetBooking)">
              <dt>Package</dt>
              <dd>{{ cardPackage(sheetBooking) }}</dd>
            </div>
            <div>
              <dt>Service</dt>
              <dd>{{ cardServiceName(sheetBooking) }}</dd>
            </div>
            <div>
              <dt>With</dt>
              <dd>{{ staffName(sheetBooking.resourceId) }}</dd>
            </div>
            <div v-if="customerPhone(sheetBooking)">
              <dt>Phone</dt>
              <dd>
                <a :href="'tel:' + customerPhone(sheetBooking)">{{ customerPhone(sheetBooking) }}</a>
              </dd>
            </div>
            <div v-if="customerEmail(sheetBooking)">
              <dt>Email</dt>
              <dd>
                <a :href="'mailto:' + customerEmail(sheetBooking)">{{ customerEmail(sheetBooking) }}</a>
              </dd>
            </div>
            <div v-if="sheetBooking.notes">
              <dt>Notes</dt>
              <dd>{{ sheetBooking.notes }}</dd>
            </div>
          </dl>

          <div class="bv2-actions">
            <button
              v-if="isRequest(sheetBooking)"
              type="button"
              class="bv2-primary"
              :disabled="busy"
              @click="doConfirm"
            >
              Confirm booking
            </button>
            <button
              v-if="isUnpaid(sheetBooking)"
              type="button"
              class="bv2-primary"
              :disabled="busy"
              @click="askPaid"
            >
              Mark as paid
            </button>
            <button
              v-if="isLiveBooking(sheetBooking)"
              type="button"
              class="bv2-choice is-ghost"
              :disabled="busy"
              @click="openBookAgain"
            >
              <strong>Book again</strong>
              <span>Same person, new time</span>
            </button>
            <button
              v-if="isLiveBooking(sheetBooking)"
              type="button"
              class="bv2-choice is-danger"
              :disabled="busy"
              @click="askCancel"
            >
              <strong>Cancel booking</strong>
              <span>Takes them off the book</span>
            </button>
          </div>
          <button type="button" class="bv2-close" @click="closeSheet">Close</button>
        </div>
      </div>
    </transition>

    <transition name="bv2-overlay">
      <div v-if="sheet && sheet.type === 'book'" class="bv2-scrim" @click.self="closeSheet">
        <div class="bv2-sheet" role="dialog" aria-modal="true" aria-label="New booking">
          <button type="button" class="bv2-sheet-handle" aria-label="Close" @click="closeSheet">
            <i />
          </button>
          <header class="bv2-sheet-head is-plain">
            <div>
              <p class="bv2-sheet-kicker">{{ dayTitle }}</p>
              <h2>New booking</h2>
            </div>
          </header>

          <p class="bv2-section">Staff</p>
          <div class="bv2-picks">
            <button
              v-for="person in bookStaff"
              :key="'s-' + person._id"
              type="button"
              class="bv2-pick"
              :class="{ 'is-on': draft.resourceId === person._id }"
              @click="setStaff(person)"
            >
              <div class="bv2-face" :style="{ background: tone(person._id) }">
                <img
                  v-if="person.image && !brokenImages[person._id]"
                  :src="person.image"
                  :alt="person.name"
                  @error="markBroken(person._id)"
                />
                <span v-else>{{ staffInitials(person.name) }}</span>
              </div>
              <strong>{{ person.name }}</strong>
            </button>
          </div>

          <template v-if="draft.resourceId">
            <p class="bv2-section">Service</p>
            <div class="bv2-stack">
              <button
                v-for="service in bookServices"
                :key="'sv-' + service._id"
                type="button"
                class="bv2-choice"
                :class="{ 'is-on': draft.serviceId === service._id }"
                @click="setService(service)"
              >
                <strong>{{ service.name }}</strong>
                <span>{{ serviceHint(service) }}</span>
              </button>
            </div>
          </template>

          <template v-if="draft.serviceId">
            <p class="bv2-section">Package</p>
            <div class="bv2-stack">
              <button
                v-for="pkg in bookPackages"
                :key="'pk-' + pkg._id"
                type="button"
                class="bv2-choice"
                :class="{ 'is-on': draft.servicePackageId === pkg._id }"
                @click="setPackage(pkg)"
              >
                <strong>{{ pkg.name }}</strong>
                <span>{{ formatEuro(pkg.price) }}</span>
              </button>
            </div>
          </template>

          <template v-if="draft.servicePackageId">
            <p class="bv2-section">When</p>
            <p v-if="loadingTimes" class="bv2-muted">Checking the book…</p>
            <p v-else-if="!availableDates.length" class="bv2-muted">
              {{ staffName(draft.resourceId) }} is fully booked.
            </p>
            <template v-else>
              <div class="bv2-dates">
                <button
                  v-for="date in dateChoices"
                  :key="'dt-' + date"
                  type="button"
                  class="bv2-date"
                  :class="{ 'is-on': selectedBookDate === date }"
                  @click="setBookDate(date)"
                >
                  {{ dateChip(date) }}
                </button>
                <button
                  v-if="dateChoices.length < availableDates.length"
                  type="button"
                  class="bv2-date is-more"
                  @click="showMoreDates"
                >
                  More
                </button>
              </div>
              <p v-if="selectedBookDate && !timeChoices.length" class="bv2-muted">
                No times left on that day.
              </p>
              <div v-for="part in timeParts" :key="part.key" class="bv2-times">
                <p v-if="part.times.length">{{ part.label }}</p>
                <div class="bv2-slots">
                  <button
                    v-for="time in part.times"
                    :key="part.key + time"
                    type="button"
                    class="bv2-slot"
                    :class="{ 'is-on': selectedBookTime === time }"
                    @click="setBookTime(time)"
                  >
                    {{ time }}
                  </button>
                </div>
              </div>
            </template>
          </template>

          <template v-if="canAskCustomer">
            <p class="bv2-section">Customer</p>
            <label class="bv2-field">
              Name
              <input v-model="draft.customer.name" type="text" autocomplete="name" />
            </label>
            <label class="bv2-field">
              Phone
              <input v-model="draft.customer.phoneNumber" type="tel" autocomplete="tel" />
            </label>
            <label class="bv2-field">
              Email
              <input v-model="draft.customer.emailAddress" type="email" autocomplete="email" />
            </label>
            <label class="bv2-field">
              Notes
              <textarea v-model="draft.notes" rows="3" />
            </label>
            <label class="bv2-field">
              Payment
              <select v-model="draft.paymentStatus">
                <option value="NOT_PAID">Not paid</option>
                <option value="PAID">Paid</option>
              </select>
            </label>
            <button
              type="button"
              class="bv2-primary"
              :disabled="busy || !canSaveBook"
              @click="saveBook"
            >
              Save booking
            </button>
          </template>

          <button type="button" class="bv2-close" @click="closeSheet">Close</button>
        </div>
      </div>
    </transition>

    <transition name="bv2-overlay">
      <div v-if="sheet && sheet.type === 'find'" class="bv2-scrim" @click.self="closeSheet">
        <div class="bv2-sheet" role="dialog" aria-modal="true" aria-label="Find a booking">
          <button type="button" class="bv2-sheet-handle" aria-label="Close" @click="closeSheet">
            <i />
          </button>
          <header class="bv2-sheet-head is-plain">
            <div>
              <p class="bv2-sheet-kicker">Find</p>
              <h2>Look someone up</h2>
            </div>
          </header>
          <form class="bv2-find" @submit.prevent="runSearchNow">
            <label class="bv2-field">
              Name, email, phone, staff or service
              <input
                v-model="findQuery"
                type="search"
                autofocus
                autocomplete="off"
                placeholder="Partial names and Irish numbers work"
              />
            </label>
          </form>
          <p v-if="findQuery.trim() && findQuery.trim().length < 2" class="bv2-muted">
            Type at least 2 characters.
          </p>
          <p v-else-if="findSearched && !findResults.length" class="bv2-muted">
            Nothing matched that. Try part of the name or a phone number.
          </p>
          <div class="bv2-stack">
            <button
              v-for="hit in findResults"
              :key="'f-' + hit._id"
              type="button"
              class="bv2-choice"
              @click="openSearchHit(hit)"
            >
              <strong>{{ hit.name || "Customer" }}</strong>
              <span>{{ findHitLine(hit) }}</span>
              <span v-if="hit.serviceName || hit.employeeName">{{ findHitService(hit) }}</span>
            </button>
          </div>
          <button type="button" class="bv2-close" @click="closeSheet">Close</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ColorHash from "color-hash";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { db, docFromSnapshot } from "@/firebase";
import bookingsStore, {
  DEFAULT_ADMIN_BOOKING,
  paymentStatusTypes
} from "@/store/bookings";
import {
  errorMessage,
  formatMatchedIn
} from "@/helpers/bookingSearch";
import {
  addDaysIso,
  mondayOfWeek,
  monthDayLabel,
  shortDayLabel,
  staffInitials,
  weekDates
} from "@/helpers/rosterV2";
import {
  aroundNowBooking,
  bookingKind,
  bookingKindLabel,
  bookingLayout,
  bookingMinutes,
  bookingsForColumn,
  clickTimeFromRatio,
  colorWash,
  customerEmail,
  customerName,
  customerPhone,
  dayInsight,
  dayWindow,
  durationMinutes,
  formatDayInsight,
  formatEuro,
  incomingBookings,
  isLiveBooking,
  minutesFromMidnight,
  isRequest,
  isUnpaid,
  liveBookingsOnDate,
  nowLinePct,
  nowScrollTop,
  packageOptions,
  serviceLine,
  serviceParts,
  servicesForResource,
  shopHoursForDate,
  staffOnDate,
  splitDayParts,
  subscribeRange,
  timeMarks,
  timeRangeLabel,
  bookableStaff
} from "@/helpers/bookingsV2";

const faceColor = new ColorHash({
  hue: { min: 12, max: 340 },
  lightness: [0.36, 0.4, 0.46],
  saturation: [0.2, 0.26, 0.32]
});

function emptyCustomer() {
  return { name: "", emailAddress: "", phoneNumber: "" };
}

export default {
  name: "BookingsV2",
  data() {
    const today = moment.utc().format("YYYY-MM-DD");
    return {
      today,
      selectedDate: today,
      weekStart: mondayOfWeek(today),
      mode: this.defaultMode(),
      userChoseMode: false,
      loading: true,
      busy: false,
      ready: false,
      moreOpen: false,
      dayDir: "next",
      sheet: null,
      ask: null,
      draft: null,
      findQuery: "",
      findResults: [],
      findSearched: false,
      findTimer: null,
      loadingTimes: false,
      dateLimit: 8,
      timeHint: "",
      brokenImages: {},
      nowTick: moment.utc().format("HH:mm"),
      nowTimer: null,
      viewportH: typeof window === "undefined" ? 900 : window.innerHeight,
      followNow: true,
      ignoreNowScroll: false
    };
  },
  computed: {
    ...mapGetters({
      bookingsMap: "bookings/bookings",
      employeesList: "employees/employeesList",
      employees: "employees/employees",
      services: "services/services",
      servicesList: "services/servicesList",
      shop: "shops/shop",
      shopId: "shops/shopId",
      availableDates: "bookings/availableDates",
      availableTimesByDate: "bookings/availableTimesByDate"
    }),
    bookings() {
      return Object.values(this.bookingsMap || {});
    },
    staff() {
      return bookableStaff(this.employeesList);
    },
    dayStaff() {
      return staffOnDate(this.staff, this.bookings, this.selectedDate);
    },
    bookStaff() {
      const mode = this.shop && this.shop.resourceAnyMode;
      if (mode === "Only Allow Any") {
        return (this.employeesList || []).filter(person => person && person._id === "Any");
      }
      return this.employeesList || [];
    },
    week() {
      return weekDates(this.weekStart);
    },
    shopName() {
      return (this.shop && this.shop.name) || "Bookings";
    },
    dayTitle() {
      return moment.utc(this.selectedDate, "YYYY-MM-DD", true).format("dddd D MMMM");
    },
    dayCounts() {
      const map = {};
      this.week.forEach(date => {
        map[date] = dayInsight({ bookings: this.bookings, date });
      });
      return map;
    },
    selectedInsight() {
      return this.dayCounts[this.selectedDate] || dayInsight({
        bookings: this.bookings,
        date: this.selectedDate
      });
    },
    dayInsightText() {
      return formatDayInsight(this.selectedInsight, {
        open: this.isOpen(this.selectedDate)
      });
    },
    incoming() {
      return incomingBookings(this.bookings);
    },
    showDock() {
      return this.incoming.length > 0 && !this.loading;
    },
    window() {
      return dayWindow({
        shop: this.shop,
        date: this.selectedDate,
        bookings: liveBookingsOnDate(this.bookings, this.selectedDate)
      });
    },
    marks() {
      return timeMarks(this.window);
    },
    nowPct() {
      return nowLinePct(this.window, this.nowTick);
    },
    dayList() {
      return liveBookingsOnDate(this.bookings, this.selectedDate).sort((a, b) =>
        String(a.start).localeCompare(String(b.start))
      );
    },
    gridStyle() {
      const hours = Math.max(1, this.window.span / 60);
      const available = Math.max(640, this.viewportH - 210);
      const hourPx = Math.max(156, available / hours);
      return {
        gridTemplateColumns: `68px repeat(${Math.max(1, this.dayStaff.length)}, minmax(220px, 1fr))`,
        "--rail": `${hours * hourPx}px`,
        "--staff": String(this.dayStaff.length)
      };
    },
    dayMotion() {
      return this.dayDir === "prev" ? "bv2-week-prev" : "bv2-week-next";
    },
    sheetBooking() {
      if (!this.sheet || this.sheet.type !== "booking") return {};
      return (
        this.bookings.find(item => item._id === this.sheet.id) ||
        this.sheet.snapshot ||
        {}
      );
    },
    sheetCustomer() {
      return customerName(this.sheetBooking);
    },
    sheetWhen() {
      const date = this.sheetBooking.start
        ? moment.utc(this.sheetBooking.start).format("dddd D MMMM")
        : this.dayTitle;
      const range = timeRangeLabel(this.sheetBooking);
      return range ? `${date} · ${range}` : date;
    },
    bookServices() {
      return servicesForResource(this.servicesList, this.draft && this.draft.resourceId);
    },
    bookPackages() {
      const service =
        this.draft && this.draft.serviceId
          ? this.services[this.draft.serviceId]
          : null;
      return packageOptions(service);
    },
    selectedBookDate() {
      if (!this.draft || !this.draft.start) return "";
      return moment.utc(this.draft.start).format("YYYY-MM-DD");
    },
    selectedBookTime() {
      if (!this.draft || !this.draft.end) return "";
      return moment.utc(this.draft.start).format("HH:mm");
    },
    dateChoices() {
      return (this.availableDates || []).slice(0, this.dateLimit);
    },
    timeChoices() {
      if (!this.selectedBookDate) return [];
      return this.availableTimesByDate(this.selectedBookDate) || [];
    },
    timeParts() {
      const parts = splitDayParts(this.timeChoices);
      return [
        { key: "am", label: "Morning", times: parts.morning },
        { key: "pm", label: "Afternoon", times: parts.afternoon },
        { key: "eve", label: "Evening", times: parts.evening }
      ];
    },
    canAskCustomer() {
      return !!(this.draft && this.draft.start && this.draft.end);
    },
    canSaveBook() {
      if (!this.canAskCustomer || !this.draft) return false;
      const name = (this.draft.customer.name || "").trim();
      const phone = (this.draft.customer.phoneNumber || "").trim();
      return !!(name && phone);
    }
  },
  watch: {
    weekStart() {
      this.listenWeek();
    },
    sheet() {
      this.lockPageScroll();
    },
    ask() {
      this.lockPageScroll();
    },
    selectedDate() {
      this.followNow = this.selectedDate === this.today;
      this.$nextTick(() => this.scrollToNow());
    },
    loading(value) {
      if (!value) this.$nextTick(() => this.scrollToNow());
    },
    dayStaff() {
      if (this.followNow) this.$nextTick(() => this.scrollToNow());
    },
    dayList() {
      if (this.followNow) this.$nextTick(() => this.scrollToNow());
    },
    nowTick() {
      if (this.followNow) this.scrollToNow();
    },
    findQuery() {
      if (!this.sheet || this.sheet.type !== "find") return;
      clearTimeout(this.findTimer);
      this.findTimer = setTimeout(() => {
        this.runSearch();
      }, 220);
    }
  },
  methods: {
    ...mapActions({
      fetchEmployees: "employees/fetchEmployees",
      fetchAllWithAnyEmployee: "employees/fetchAllWithAnyEmployee",
      fetchAnyEmployee: "employees/fetchAnyEmployee",
      fetchServices: "services/fetchServices",
      fetchShop: "shops/fetchShop",
      subscribeBookings: "bookings/subscribeBookings",
      unsubscribeBookings: "bookings/unsubscribeBookings",
      fetchAvailableTimes: "bookings/fetchAvailableTimes",
      createAdminBooking: "bookings/createAdminBooking",
      confirmBooking: "bookings/confirmBooking",
      markBookingAsPaid: "bookings/markBookingAsPaid",
      cancelBooking: "bookings/cancelBooking"
    }),
    bookingKind,
    bookingKindLabel,
    customerName,
    customerPhone,
    customerEmail,
    formatEuro,
    timeRangeLabel,
    isLiveBooking,
    isRequest,
    isUnpaid,
    bookingMinutes,
    staffInitials,
    shortDay: shortDayLabel,
    monthDay: monthDayLabel,
    defaultMode() {
      if (typeof window === "undefined") return "board";
      return window.innerWidth < 768 ? "list" : "board";
    },
    tone(id) {
      return faceColor.hex(String(id || "staff"));
    },
    laneStyle(id) {
      const hex = this.tone(id);
      return {
        "--lane": hex,
        "--lane-wash": colorWash(hex, 0.08),
        "--lane-soft": colorWash(hex, 0.16)
      };
    },
    markBroken(id) {
      this.$set(this.brokenImages, id, true);
    },
    isOpen(date) {
      return !!shopHoursForDate(this.shop, date);
    },
    stripClass(date) {
      const cover = this.dayCounts[date] || {};
      return {
        "is-today": date === this.today,
        "is-selected": date === this.selectedDate,
        "is-shut": !this.isOpen(date),
        "is-hot": cover.requests > 0
      };
    },
    columnBookings(resourceId) {
      return bookingsForColumn(this.bookings, resourceId, this.selectedDate);
    },
    columnCount(resourceId) {
      const count = this.columnBookings(resourceId).length;
      if (!count) return "Free";
      return count === 1 ? "1 booked" : `${count} booked`;
    },
    staffName(id) {
      const person = this.employees[id];
      return person && person.name ? person.name : "Staff";
    },
    staffImage(id) {
      const person = this.employees[id];
      if (!person || !person.image || this.brokenImages[id]) return "";
      return person.image;
    },
    cardParts(item) {
      const service = item && this.services[item.serviceId];
      const pkg =
        service && service.packages && item.servicePackageId
          ? service.packages[item.servicePackageId]
          : null;
      return serviceParts(service, pkg);
    },
    cardPackage(item) {
      return this.cardParts(item).package;
    },
    cardServiceName(item) {
      return this.cardParts(item).service;
    },
    cardService(item) {
      const parts = this.cardParts(item);
      return serviceLine({ name: parts.service }, parts.package ? { name: parts.package } : null);
    },
    cardStyle(item) {
      const layout = this.cardLayout(item);
      return {
        top: `calc(${layout.top}% + 4px)`,
        height: `calc(${layout.height}% - 8px)`
      };
    },
    cardLayout(item) {
      return bookingLayout(item, this.window);
    },
    apptClass(item) {
      const mins = bookingMinutes(item);
      return {
        ["is-" + bookingKind(item)]: true,
        "is-short": mins < 25,
        "is-tiny": mins < 15
      };
    },
    cardAria(item, person) {
      return `${customerName(item)}, ${timeRangeLabel(item)}, ${this.cardService(item)}, ${person.name}`;
    },
    shortWhen(item) {
      const date = moment.utc(item.start).format("ddd D");
      return `${date} ${timeRangeLabel(item)}`;
    },
    dateChip(date) {
      return moment.utc(date, "YYYY-MM-DD", true).format("ddd D MMM");
    },
    serviceHint(service) {
      const mins = durationMinutes(service);
      return mins ? `${mins} min` : "Service";
    },
    selectDate(date) {
      this.dayDir = date > this.selectedDate ? "next" : "prev";
      this.selectedDate = date;
      this.weekStart = mondayOfWeek(date);
    },
    shiftDay(days) {
      this.dayDir = days > 0 ? "next" : "prev";
      this.selectedDate = addDaysIso(this.selectedDate, days);
      this.weekStart = mondayOfWeek(this.selectedDate);
    },
    goToday() {
      this.selectDate(this.today);
    },
    jumpToday() {
      this.closeMore();
      this.goToday();
    },
    jumpNow() {
      this.followNow = true;
      this.scrollToNow();
    },
    runNow() {
      this.closeMore();
      this.jumpNow();
    },
    onBoardScroll() {
      if (this.ignoreNowScroll) return;
      this.followNow = false;
    },
    onUserScroll() {
      if (this.ignoreNowScroll || this.sheet || this.ask) return;
      if (this.mode === "list") this.followNow = false;
    },
    scrollToNow() {
      if (this.selectedDate !== this.today) return;
      if (typeof window === "undefined") return;
      if (this.mode === "board") {
        const board = this.$refs.board;
        if (!board || !board.querySelector) return;
        const rail = board.querySelector(".bv2-col-rail");
        if (!rail || !rail.offsetHeight) return;
        const head = board.querySelector(".bv2-colhead");
        let pct = this.nowPct;
        if (pct == null) {
          const now = minutesFromMidnight(this.nowTick);
          pct = now != null && now < this.window.startMin ? 0 : 100;
        }
        this.ignoreNowScroll = true;
        board.scrollTop = nowScrollTop({
          railHeight: rail.offsetHeight,
          nowPct: pct,
          viewHeight: board.clientHeight,
          headHeight: head ? head.offsetHeight : 0
        });
        this.$nextTick(() => {
          this.ignoreNowScroll = false;
        });
        return;
      }
      const item = aroundNowBooking(this.dayList, this.nowTick);
      if (!item) return;
      const el = this.$el && this.$el.querySelector
        ? this.$el.querySelector(`[data-booking="${item._id}"]`)
        : null;
      if (!el || !el.scrollIntoView) return;
      this.ignoreNowScroll = true;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      window.setTimeout(() => {
        this.ignoreNowScroll = false;
      }, 450);
    },
    closeMore() {
      this.moreOpen = false;
    },
    runBook() {
      this.closeMore();
      this.openBook();
    },
    runFind() {
      this.closeMore();
      this.openFind();
    },
    listenWeek() {
      this.subscribeBookings(subscribeRange(this.weekStart));
    },
    onColumnClick(event, person) {
      if (this.busy) return;
      const rail = event.currentTarget.querySelector(".bv2-col-rail");
      if (!rail) return;
      const box = rail.getBoundingClientRect();
      if (!box.height) return;
      const ratio = (event.clientY - box.top) / box.height;
      const time = clickTimeFromRatio(this.window, ratio);
      this.openBook({
        resourceId: person._id,
        date: this.selectedDate,
        time
      });
    },
    openBooking(item) {
      this.ask = null;
      this.sheet = {
        type: "booking",
        id: item._id,
        snapshot: { ...item }
      };
    },
    openBook(seed) {
      const start = this.blankDraft();
      if (seed && seed.resourceId) start.resourceId = seed.resourceId;
      if (seed && seed.date) {
        start.start = `${seed.date}T00:00:00Z`;
      } else {
        start.start = `${this.selectedDate}T00:00:00Z`;
      }
      this.timeHint = (seed && seed.time) || "";
      this.dateLimit = 8;
      this.draft = start;
      this.sheet = { type: "book" };
    },
    blankDraft() {
      return {
        ...DEFAULT_ADMIN_BOOKING,
        customer: emptyCustomer(),
        notes: "",
        paymentStatus: paymentStatusTypes.NOT_PAID,
        resourceId: null,
        serviceId: null,
        servicePackageId: null,
        start: null,
        end: null,
        total: null
      };
    },
    openBookAgain() {
      const item = this.sheetBooking;
      const draft = this.blankDraft();
      draft.resourceId = item.resourceId || null;
      draft.serviceId = item.serviceId || null;
      draft.servicePackageId = item.servicePackageId || null;
      draft.total = item.total;
      draft.customer = {
        name: customerName(item) === "Customer" ? "" : customerName(item),
        phoneNumber: customerPhone(item),
        emailAddress: customerEmail(item)
      };
      draft.notes = item.notes || "";
      draft.start = `${this.selectedDate}T00:00:00Z`;
      this.timeHint = "";
      this.dateLimit = 8;
      this.draft = draft;
      this.sheet = { type: "book" };
      if (draft.resourceId && draft.serviceId && draft.servicePackageId) {
        this.loadTimes();
      }
    },
    openFind() {
      this.findQuery = "";
      this.findResults = [];
      this.findSearched = false;
      this.sheet = { type: "find" };
      if (this.shopId) {
        bookingsStore.prefetchSearchableBookings(this.shopId);
      }
    },
    closeSheet() {
      this.sheet = null;
      this.draft = null;
      this.timeHint = "";
    },
    closeAsk() {
      this.ask = null;
    },
    setStaff(person) {
      this.draft = {
        ...this.draft,
        resourceId: person._id,
        serviceId: null,
        servicePackageId: null,
        end: null,
        total: null
      };
      if (this.draft.start && this.draft.start.indexOf("T00:00:00") === -1) {
        const date = moment.utc(this.draft.start).format("YYYY-MM-DD");
        this.draft.start = `${date}T00:00:00Z`;
      }
    },
    setService(service) {
      this.draft = {
        ...this.draft,
        serviceId: service._id,
        servicePackageId: null,
        end: null,
        total: null
      };
    },
    setPackage(pkg) {
      this.draft = {
        ...this.draft,
        servicePackageId: pkg._id,
        total: pkg.price,
        end: null
      };
      if (this.draft.start && this.draft.start.indexOf("T00:00:00") === -1) {
        const date = moment.utc(this.draft.start).format("YYYY-MM-DD");
        this.draft.start = `${date}T00:00:00Z`;
      }
      this.loadTimes();
    },
    async loadTimes() {
      if (!this.draft || !this.draft.resourceId || !this.draft.serviceId) return;
      this.loadingTimes = true;
      try {
        await this.fetchAvailableTimes({
          resourceId: this.draft.resourceId,
          serviceId: this.draft.serviceId
        });
        const preferred =
          this.selectedBookDate ||
          this.selectedDate ||
          (this.availableDates[0] || "");
        if (preferred && this.availableDates.indexOf(preferred) !== -1) {
          this.setBookDate(preferred);
        } else if (this.availableDates[0]) {
          this.setBookDate(this.availableDates[0]);
        }
      } catch (error) {
        this.$notify({
          type: "error",
          title: this.$t("message.error"),
          text: errorMessage(error, "Could not load times")
        });
      } finally {
        this.loadingTimes = false;
      }
    },
    setBookDate(date) {
      this.draft = {
        ...this.draft,
        start: `${date}T00:00:00Z`,
        end: null
      };
      const times = this.availableTimesByDate(date) || [];
      if (this.timeHint && times.indexOf(this.timeHint) !== -1) {
        this.setBookTime(this.timeHint);
      }
    },
    setBookTime(time) {
      const date = this.selectedBookDate;
      const service =
        this.draft && this.draft.serviceId
          ? this.services[this.draft.serviceId]
          : null;
      const minutes = durationMinutes(service) || 30;
      const start = moment.utc(`${date} ${time}`, "YYYY-MM-DD HH:mm");
      this.draft = {
        ...this.draft,
        start: start.format(),
        end: start.clone().add(minutes, "minute").format()
      };
    },
    showMoreDates() {
      this.dateLimit += 8;
    },
    async saveBook() {
      if (!this.canSaveBook || this.busy) return;
      const booking = {
        ...this.draft,
        customer: {
          name: (this.draft.customer.name || "").trim(),
          phoneNumber: (this.draft.customer.phoneNumber || "").trim(),
          emailAddress: (this.draft.customer.emailAddress || "").trim()
        },
        total: parseFloat(this.draft.total)
      };
      this.busy = true;
      try {
        if (booking.resourceId === "Any") {
          const allocated = await this.allocateResource(booking);
          if (!allocated) {
            this.$notify({
              type: "error",
              text: "No one is free at that time."
            });
            return;
          }
          booking.resourceId = allocated;
        }
        await this.createAdminBooking({ booking });
        this.$notify({ type: "success", text: "Booking saved" });
        this.closeSheet();
      } catch (error) {
        this.$notify({
          type: "error",
          title: this.$t("message.error"),
          text: errorMessage(error, "Could not save booking")
        });
      } finally {
        this.busy = false;
      }
    },
    async allocateResource(booking) {
      const service = this.services[booking.serviceId];
      if (!service || !Array.isArray(service.resources) || !service.resources.length) {
        return null;
      }
      const resources = this.shuffle(service.resources.slice());
      const startTime = moment.utc(booking.start).format("HH:mm");
      const startDate = moment.utc(booking.start).format("YYYY-MM-DD");
      for (let i = 0; i < resources.length; i += 1) {
        const slots = await bookingsStore.getBookingsAvailability(
          resources[i],
          service._id
        );
        if (slots && slots[startDate] && slots[startDate].indexOf(startTime) !== -1) {
          return resources[i];
        }
      }
      return null;
    },
    shuffle(list) {
      const next = list.slice();
      for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = next[i];
        next[i] = next[j];
        next[j] = tmp;
      }
      return next;
    },
    findHitLine(hit) {
      const contact = hit.number || hit.email || "";
      const when = [hit.date, hit.time].filter(Boolean).join(" ");
      return [when, contact].filter(Boolean).join(" · ");
    },
    findHitService(hit) {
      const line = [hit.serviceName, hit.employeeName].filter(Boolean).join(" · ");
      const matched = formatMatchedIn(hit.matchedIn);
      return matched ? `${line}${line ? " · " : ""}${matched}` : line;
    },
    runSearchNow() {
      clearTimeout(this.findTimer);
      this.runSearch();
    },
    async runSearch() {
      const query = (this.findQuery || "").trim();
      if (!query || query.length < 2 || !this.shopId) {
        this.findResults = [];
        this.findSearched = query.length > 0;
        return;
      }
      const showBusy = !bookingsStore.isSearchCacheReady(this.shopId);
      if (showBusy) this.busy = true;
      this.findSearched = true;
      try {
        this.findResults = await bookingsStore.searchStoreBookings(
          query,
          this.shopId,
          {
            lookups: {
              employeeById: id => (this.employees || {})[id],
              serviceById: id => (this.services || {})[id]
            }
          }
        );
      } catch (error) {
        this.findResults = [];
        this.$notify({
          type: "error",
          title: this.$t("message.error"),
          text: errorMessage(error, "Search failed")
        });
      } finally {
        this.busy = false;
      }
    },
    async openSearchHit(hit) {
      this.busy = true;
      try {
        const snap = await db.collection("bookings").doc(hit._id).get();
        const booking = docFromSnapshot(snap);
        if (!booking) {
          this.$notify({ type: "error", text: "That booking is gone." });
          return;
        }
        const date = moment.utc(booking.start).format("YYYY-MM-DD");
        this.selectedDate = date;
        this.weekStart = mondayOfWeek(date);
        this.openBooking(booking);
      } catch (error) {
        this.$notify({
          type: "error",
          text: errorMessage(error, "Could not open booking")
        });
      } finally {
        this.busy = false;
      }
    },
    async doConfirm() {
      if (this.busy) return;
      this.busy = true;
      try {
        await this.confirmBooking(this.sheetBooking);
        this.$notify({ type: "success", text: "Booking confirmed" });
      } catch (error) {
        this.$notify({
          type: "error",
          text: errorMessage(error, "Could not confirm")
        });
      } finally {
        this.busy = false;
      }
    },
    askPaid() {
      this.ask = {
        action: "paid",
        booking: this.sheetBooking,
        kicker: customerName(this.sheetBooking),
        title: "Mark as paid?",
        body: "This booking will show as paid.",
        actionLabel: "Mark paid"
      };
    },
    askCancel() {
      this.ask = {
        action: "cancel",
        booking: this.sheetBooking,
        kicker: customerName(this.sheetBooking),
        title: "Cancel this booking?",
        body: "They come off the book. This cannot be undone here.",
        actionLabel: "Cancel booking"
      };
    },
    async confirmAsk() {
      if (!this.ask || this.busy) return;
      this.busy = true;
      try {
        if (this.ask.action === "paid") {
          await this.markBookingAsPaid(this.ask.booking);
          this.$notify({ type: "success", text: "Marked as paid" });
        } else if (this.ask.action === "cancel") {
          await this.cancelBooking(this.ask.booking);
          this.$notify({ type: "success", text: "Booking cancelled" });
          this.closeSheet();
        }
        this.closeAsk();
      } catch (error) {
        this.$notify({
          type: "error",
          text: errorMessage(error, "Could not update booking")
        });
      } finally {
        this.busy = false;
      }
    },
    lockPageScroll() {
      if (typeof document === "undefined") return;
      document.body.style.overflow = this.sheet || this.ask ? "hidden" : "";
    },
    onKeydown(event) {
      if (event.key !== "Escape") return;
      if (this.ask) {
        this.closeAsk();
        return;
      }
      this.closeSheet();
    },
    onResize() {
      this.viewportH = window.innerHeight;
      if (!this.userChoseMode) this.mode = this.defaultMode();
      if (this.followNow) this.$nextTick(() => this.scrollToNow());
    },
    tickNow() {
      this.nowTick = moment.utc().format("HH:mm");
    },
    paintChrome(on) {
      if (typeof document === "undefined") return;
      const panel = document.querySelector(".main-panel");
      if (panel) panel.classList.toggle("bv2-surface", on);
      document.body.classList.toggle("bv2-surface", on);
    },
    teardownChrome() {
      this.paintChrome(false);
      document.body.style.overflow = "";
      window.removeEventListener("keydown", this.onKeydown);
      window.removeEventListener("resize", this.onResize);
      window.removeEventListener("scroll", this.onUserScroll);
      if (this.nowTimer) {
        clearInterval(this.nowTimer);
        this.nowTimer = null;
      }
    },
    async loadPeople() {
      const mode = this.shop && this.shop.resourceAnyMode;
      if (mode === "Only Allow Any") {
        await this.fetchAllWithAnyEmployee();
        return;
      }
      if (mode === "On") {
        await this.fetchAllWithAnyEmployee();
        return;
      }
      await this.fetchEmployees();
    }
  },
  async created() {
    this.paintChrome(true);
    try {
      await Promise.all([this.fetchShop(), this.fetchServices()]);
      await this.loadPeople();
      this.listenWeek();
    } catch (error) {
      this.$notify({
        type: "error",
        title: this.$t("message.error"),
        text: errorMessage(error, "Could not load")
      });
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onResize);
    window.addEventListener("scroll", this.onUserScroll, { passive: true });
    this.nowTimer = setInterval(this.tickNow, 60000);
    this.$nextTick(() => this.scrollToNow());
    this.$nextTick(() => {
      this.ready = true;
    });
  },
  beforeDestroy() {
    clearTimeout(this.findTimer);
    this.unsubscribeBookings();
    this.teardownChrome();
  },
  beforeRouteLeave(to, from, next) {
    this.teardownChrome();
    next();
  }
};
</script>

<style scoped>
.bv2 {
  --ink: #14110e;
  --ink-soft: #3c352e;
  --muted: #7a7268;
  --faint: #b3aaa0;
  --line: rgba(20, 17, 14, 0.08);
  --line-strong: rgba(20, 17, 14, 0.14);
  --canvas: #efe8dc;
  --surface: #fbf7f0;
  --surface-2: #fffdf8;
  --booked: #d7e4d2;
  --booked-ink: #1c4631;
  --unpaid: #f3d6c3;
  --unpaid-ink: #8a3c14;
  --request: #f6ead0;
  --request-ink: #6a4a12;
  --today: #f6ead0;
  --shadow: 0 1px 0 rgba(255, 255, 255, 0.65) inset, 0 18px 40px rgba(42, 28, 14, 0.07),
    0 2px 6px rgba(42, 28, 14, 0.04);
  --ease: cubic-bezier(0.22, 1, 0.36, 1);
  --font: "Instrument Sans", "Montserrat", "Helvetica Neue", sans-serif;
  --display: "Instrument Serif", "Iowan Old Style", Georgia, serif;
  position: relative;
  max-width: none;
  margin: -86px -15px 0;
  min-height: calc(100vh - 62px);
  padding: 12px 16px 88px;
  color: var(--ink);
  font-family: var(--font);
  background:
    radial-gradient(900px 320px at 8% -10%, rgba(255, 255, 255, 0.7), transparent 58%),
    radial-gradient(720px 260px at 100% 0%, rgba(232, 214, 186, 0.4), transparent 52%),
    var(--canvas);
}

.bv2-progress {
  position: sticky;
  top: 0;
  z-index: 30;
  height: 3px;
  overflow: hidden;
  background: rgba(20, 17, 14, 0.08);
}

.bv2-progress i {
  display: block;
  height: 100%;
  width: 40%;
  background: var(--ink);
  animation: bv2-slide 1.1s var(--ease) infinite;
}

@keyframes bv2-slide {
  from { transform: translateX(-120%); }
  to { transform: translateX(320%); }
}

.bv2-top-desktop {
  display: none;
}

.bv2-phone {
  display: block;
  margin-bottom: 12px;
}

.bv2-phone-week {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.bv2-phone-week-copy span,
.bv2-kicker {
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.bv2-kicker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bv2-kicker i,
.bv2-phone-week-copy span {
  font-style: normal;
}

.bv2-kicker i {
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(20, 17, 14, 0.08);
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.bv2-phone-week-copy strong,
.bv2-title {
  display: block;
  font-family: var(--display);
  font-size: 28px;
  font-weight: 400;
  letter-spacing: -0.03em;
}

.bv2-phone-status,
.bv2-sub {
  margin: 6px 0 0;
  color: var(--ink-soft);
  font-size: 14px;
}

.bv2-more {
  position: relative;
}

.bv2-more-btn,
.bv2-chev,
.bv2-chip-btn,
.bv2-close,
.bv2-choice,
.bv2-card,
.bv2-strip-day,
.bv2-appt,
.bv2-pick,
.bv2-date,
.bv2-slot,
.bv2-dock-item,
.bv2-empty-card,
.bv2-primary {
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  touch-action: manipulation;
}

.bv2-more-btn,
.bv2-chev {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 253, 248, 0.55);
  box-shadow: inset 0 0 0 1px var(--line);
}

.bv2-more-btn svg,
.bv2-chev svg {
  display: block;
  width: 20px;
  height: 20px;
  margin: 0 auto;
}

.bv2-more-menu {
  position: absolute;
  top: 48px;
  right: 0;
  z-index: 20;
  min-width: 180px;
  padding: 8px;
  border-radius: 18px;
  background: var(--surface-2);
  box-shadow: var(--shadow);
}

.bv2-more-menu button,
.bv2-more-menu a {
  display: block;
  width: 100%;
  padding: 10px 12px;
  border-radius: 12px;
  color: var(--ink);
  text-align: left;
  text-decoration: none;
}

.bv2-strip {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 14px;
}

.bv2-strip-day {
  display: grid;
  gap: 2px;
  min-height: 64px;
  padding: 8px 2px;
  border-radius: 18px;
  background: var(--surface);
  box-shadow: inset 0 0 0 1px var(--line);
  text-align: center;
}

.bv2-strip-day span {
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.bv2-strip-day b {
  font-size: 16px;
}

.bv2-strip-day em {
  color: var(--muted);
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
}

.bv2-strip-day.is-today {
  background: linear-gradient(180deg, var(--today), var(--surface) 80%);
}

.bv2-strip-day.is-selected {
  box-shadow: inset 0 0 0 1.5px var(--ink);
}

.bv2-strip-day.is-hot em {
  color: var(--request-ink);
}

.bv2-strip-day.is-shut {
  opacity: 0.55;
}

.bv2-loading,
.bv2-empty,
.bv2-muted {
  color: var(--muted);
  font-size: 14px;
}

.bv2-empty,
.bv2-loading {
  padding: 48px 12px;
  text-align: center;
}

.bv2-board {
  display: none;
}

.bv2-dayboard {
  display: grid;
  gap: 12px;
}

.bv2-card,
.bv2-empty-card {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) 52px;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border-radius: 22px;
  background: var(--surface);
  box-shadow: inset 4px 0 0 var(--lane, transparent), var(--shadow);
  text-align: left;
}

.bv2-empty-card {
  grid-template-columns: 1fr;
}

.bv2-card.is-booked {
  background: var(--booked);
  color: var(--booked-ink);
}

.bv2-card.is-unpaid {
  background: var(--unpaid);
  color: var(--unpaid-ink);
}

.bv2-card.is-request {
  background: var(--request);
  color: var(--request-ink);
}

.bv2-card-who {
  min-width: 0;
}

.bv2-card-when b,
.bv2-card-who strong {
  display: block;
}

.bv2-card-when span,
.bv2-card-who span {
  display: block;
  margin-top: 2px;
  opacity: 0.75;
  font-size: 12px;
}

.bv2-card em {
  font-style: normal;
  font-weight: 700;
}

.bv2-dock {
  position: sticky;
  bottom: 8px;
  z-index: 15;
  margin-top: 16px;
  padding: 12px;
  border-radius: 22px;
  background: var(--request);
  color: var(--request-ink);
  box-shadow: var(--shadow);
}

.bv2-dock-bar p {
  margin: 0 0 8px;
}

.bv2-dock-list {
  display: grid;
  gap: 6px;
}

.bv2-dock-item {
  display: grid;
  width: 100%;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 253, 248, 0.55);
  text-align: left;
}

.bv2-dock-item span {
  color: var(--muted);
  font-size: 12px;
}

.bv2-scrim {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(20, 16, 12, 0.38);
  backdrop-filter: blur(10px);
}

.bv2-sheet {
  width: min(520px, 100%);
  max-height: min(92vh, 820px);
  overflow-y: auto;
  padding: 8px 18px calc(18px + env(safe-area-inset-bottom));
  border-radius: 28px 28px 0 0;
  background: var(--surface-2);
  box-shadow: 0 -24px 80px rgba(20, 17, 14, 0.22);
}

.bv2-sheet-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 28px;
  margin: 0 0 8px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.bv2-sheet-handle i {
  display: block;
  width: 42px;
  height: 4px;
  border-radius: 99px;
  background: #d8d0c4;
}

.bv2-sheet-head {
  display: flex;
  gap: 14px;
  align-items: center;
}

.bv2-sheet-head.is-plain {
  display: block;
}

.bv2-sheet-kicker {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.bv2-sheet h2 {
  margin: 0 0 6px;
  font-family: var(--display);
  font-size: 30px;
  font-weight: 400;
  letter-spacing: -0.03em;
}

.bv2-sheet-now {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
}

.bv2-face {
  position: relative;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  overflow: hidden;
  border-radius: 50%;
  color: #f7f1e8;
  font-size: 11px;
  font-weight: 700;
  line-height: 34px;
  text-align: center;
}

.bv2-face img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bv2-face.is-lg {
  width: 52px;
  height: 52px;
  font-size: 16px;
  line-height: 52px;
}

.bv2-face.is-card {
  width: 48px;
  height: 48px;
  font-size: 13px;
  line-height: 48px;
  box-shadow: 0 0 0 3px var(--lane-soft, rgba(20, 17, 14, 0.08)),
    0 8px 16px rgba(20, 17, 14, 0.12);
}

.bv2-facts {
  display: grid;
  gap: 10px;
  margin: 18px 0;
}

.bv2-facts div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.bv2-facts dt {
  color: var(--muted);
  font-size: 13px;
}

.bv2-facts dd {
  margin: 0;
  text-align: right;
  font-weight: 600;
}

.bv2-facts a {
  color: inherit;
}

.bv2-actions,
.bv2-stack {
  display: grid;
  gap: 8px;
}

.bv2-choice {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 68px;
  padding: 12px 14px;
  border-radius: 18px;
  background: #f3eee6;
  text-align: left;
}

.bv2-choice.is-on,
.bv2-pick.is-on,
.bv2-date.is-on,
.bv2-slot.is-on {
  box-shadow: inset 0 0 0 1.5px var(--ink);
}

.bv2-choice.is-danger {
  background: var(--unpaid);
  color: var(--unpaid-ink);
}

.bv2-choice span {
  color: var(--muted);
  font-size: 12px;
}

.bv2-primary {
  width: 100%;
  min-height: 48px;
  margin-top: 8px;
  border-radius: 999px;
  background: var(--ink);
  color: #f6efe4;
  font-weight: 600;
}

.bv2-primary.is-danger {
  background: #8a3c14;
}

.bv2-primary:disabled {
  opacity: 0.45;
}

.bv2-close {
  width: 100%;
  min-height: 44px;
  margin: 8px 0 4px;
  color: var(--muted);
}

.bv2-section {
  margin: 18px 0 8px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.bv2-picks {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(92px, 1fr));
  gap: 8px;
}

.bv2-pick {
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 10px 8px;
  border-radius: 18px;
  background: #f3eee6;
}

.bv2-pick strong {
  font-size: 12px;
}

.bv2-dates,
.bv2-slots {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bv2-date,
.bv2-slot {
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #f3eee6;
  font-size: 13px;
  font-weight: 600;
}

.bv2-times p {
  margin: 12px 0 6px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.bv2-field {
  display: grid;
  gap: 6px;
  margin-bottom: 10px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.bv2-field input,
.bv2-field textarea,
.bv2-field select {
  min-height: 48px;
  padding: 8px 12px;
  border: 1px solid var(--line-strong);
  border-radius: 14px;
  background: #fff;
  color: var(--ink);
  font: inherit;
}

.bv2-ask-body {
  color: var(--ink-soft);
}

.bv2-overlay-enter-active,
.bv2-overlay-leave-active,
.bv2-fade-enter-active,
.bv2-fade-leave-active,
.bv2-week-next-enter-active,
.bv2-week-next-leave-active,
.bv2-week-prev-enter-active,
.bv2-week-prev-leave-active,
.bv2-pop-enter-active,
.bv2-pop-leave-active {
  transition: opacity 0.22s var(--ease), transform 0.28s var(--ease);
}

.bv2-overlay-enter,
.bv2-overlay-leave-to {
  opacity: 0;
}

.bv2-overlay-enter .bv2-sheet,
.bv2-overlay-leave-to .bv2-sheet {
  transform: translateY(18px);
}

.bv2-fade-enter,
.bv2-fade-leave-to,
.bv2-pop-enter,
.bv2-pop-leave-to {
  opacity: 0;
}

.bv2-week-next-enter {
  opacity: 0;
  transform: translateX(12px);
}

.bv2-week-next-leave-to,
.bv2-week-prev-enter {
  opacity: 0;
  transform: translateX(-12px);
}

.bv2-week-prev-leave-to {
  opacity: 0;
  transform: translateX(12px);
}

@media (min-width: 768px) {
  .bv2 {
    margin: -86px -30px 0;
    padding: 10px 20px 28px;
  }

  .bv2-phone {
    display: none;
  }

  .bv2-top-desktop {
    display: block;
  }

  .bv2-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }

  .bv2-bar-copy {
    flex: 1;
    min-width: 0;
  }

  .bv2-title {
    margin: 0;
    font-size: 26px;
    line-height: 1.1;
  }

  .bv2-sub {
    margin: 2px 0 0;
    font-size: 13px;
  }

  .bv2-bar-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  .bv2-chip-btn {
    min-height: 38px;
    padding: 0 14px;
    border-radius: 999px;
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--line);
    font-size: 13px;
    font-weight: 600;
  }

  .bv2-chip-btn.is-strong {
    background: var(--ink);
    color: #f6efe4;
    box-shadow: none;
  }

  .bv2-chip-btn.is-on {
    box-shadow: inset 0 0 0 1.5px var(--ink);
  }

  .bv2-classic {
    color: var(--muted);
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    border-bottom: 1px solid var(--line-strong);
  }

  .bv2-strip {
    margin-bottom: 10px;
  }

  .bv2-strip-day {
    min-height: 54px;
    padding: 6px 2px;
  }

  .bv2-dayboard {
    display: none;
  }

  .bv2-board {
    display: block;
    overflow: auto;
    max-height: calc(100vh - 210px);
    border-radius: 20px;
    background: var(--surface);
    box-shadow: var(--shadow);
  }

  .bv2-board-empty {
    display: grid;
    gap: 4px;
    min-height: 220px;
    place-content: center;
    padding: 32px;
    color: var(--muted);
    text-align: center;
  }

  .bv2-board-empty strong {
    color: var(--ink);
    font-size: 18px;
  }

  .bv2-grid {
    display: grid;
    min-width: calc(68px + var(--staff) * 196px);
  }

  .bv2-corner,
  .bv2-colhead {
    position: sticky;
    top: 0;
    z-index: 4;
    background: var(--surface-2);
    box-shadow: inset 0 -1px 0 var(--line);
  }

  .bv2-corner {
    padding: 10px 8px;
    color: var(--muted);
    font-size: 11px;
    font-weight: 700;
  }

  .bv2-colhead {
    display: flex;
    gap: 10px;
    align-items: center;
    min-height: 56px;
    padding: 8px 10px;
    background:
      linear-gradient(180deg, var(--lane-soft), var(--surface-2) 78%);
    box-shadow: inset 0 -2px 0 var(--lane);
  }

  .bv2-colhead strong,
  .bv2-colhead span {
    display: block;
  }

  .bv2-colhead span {
    color: var(--muted);
    font-size: 12px;
  }

  .bv2-hours,
  .bv2-col-rail {
    position: relative;
    height: var(--rail);
  }

  .bv2-hours {
    color: var(--ink-soft);
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .bv2-hours .bv2-hour {
    position: absolute;
    left: 8px;
    transform: translateY(-50%);
    font-style: normal;
  }

  .bv2-hours .bv2-hour.is-half {
    display: none;
  }

  .bv2-now-label {
    position: absolute;
    left: 6px;
    z-index: 5;
    padding: 1px 5px;
    border-radius: 6px;
    background: #e31c23;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    transform: translateY(-50%);
  }

  .bv2-col {
    min-width: 0;
    background: var(--lane-wash);
    border-left: 1px solid var(--lane-soft);
  }

  .bv2-hline {
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--line);
  }

  .bv2-hline.is-hour {
    background: var(--line-strong);
  }

  .bv2-hline.is-half {
    background: rgba(20, 17, 14, 0.05);
  }

  .bv2-now {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 3;
    height: 2px;
    background: #e31c23;
  }

  .bv2-now::before {
    content: "";
    position: absolute;
    left: -5px;
    top: -4px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #e31c23;
  }

  .bv2-appt {
    position: absolute;
    left: 6px;
    right: 6px;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 3px;
    overflow: hidden;
    padding: 7px 9px;
    border-radius: 8px;
    text-align: left;
    box-shadow: inset 3px 0 0 currentColor, inset 0 1px 0 rgba(255, 255, 255, 0.35);
  }

  .bv2-appt-time,
  .bv2-appt-name,
  .bv2-appt-work {
    display: block;
    flex: 0 0 auto;
    max-width: 100%;
    overflow: hidden;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
    word-break: normal;
    overflow-wrap: normal;
  }

  .bv2-appt-time {
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.01em;
    opacity: 0.72;
  }

  .bv2-appt-name {
    font-size: 13px;
    font-weight: 700;
  }

  .bv2-appt-work {
    font-size: 12px;
    font-weight: 600;
    opacity: 0.9;
  }

  .bv2-appt.is-tiny .bv2-appt-work {
    display: none;
  }

  .bv2-appt.is-booked {
    background: var(--booked);
    color: var(--booked-ink);
  }

  .bv2-appt.is-unpaid {
    background: var(--unpaid);
    color: var(--unpaid-ink);
  }

  .bv2-appt.is-request {
    background: var(--request);
    color: var(--request-ink);
  }

  .bv2-dock {
    bottom: 16px;
  }

  .bv2-dock-list {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
}
</style>

<style>
.main-panel.bv2-surface {
  background: #efe8dc !important;
}

.main-panel.bv2-surface .panel-header-sm {
  background: #14110e !important;
}

.main-panel.bv2-surface .navbar-absolute {
  background: transparent !important;
}
</style>
